import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, CheckCircle2, CornerUpLeft, BarChart3,
  ArrowUpRight, User, Paperclip, Radio, FileText, Grid2x2, CircleCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CTS_WORKFLOW_LABEL, CTS_WORKFLOW_STATES, CTS_OPEN_STATES, ctsNextStates,
  CTS_IMPLEMENTATION_STATUS_LABEL, CTS_NOT_YET_ASSESSED_LABEL,
  type CtsWorkflowState, type CtsImplementationStatus,
} from '@grc/shared';
import { NO_EVIDENCE_LABEL, workflowErrorText } from '@/components/compliance/assessment/assessment-copy';
import {
  useTasksWorkspace, useTransitionTask, useEvidenceRepository,
  useExceptionsRegister, useProgramActivity,
} from '@/api/hooks/useCtsTasks';
import type { Task } from '@/api/types/cts-tasks.types';
import {
  buildInsights, workflowDistribution, postureDistribution, type DistributionSegment,
} from '@/features/compliance/tasks/insights';
import { EvidencePanel, ExceptionsPanel, ActivityPanel } from './program-panels';

/**
 * TASKS — the unified workspace (Tenant Convergence TC4, built to Design's pass).
 *
 * ⚠ ONE PAGE, FOUR TABS. Tasks, Evidence, Exceptions and Program activity are the same programme
 * seen four ways; splitting them across routes makes a reader navigate to assemble one answer.
 *
 * ⚠ PERSONAL-FIRST, AND A REVIEW I OWE IS MY WORK. "My assignments" carries both what is assigned
 * to me AND what awaits my decision — a review queue is not a separate surface, it is the other
 * half of the same question a user opens this page to ask.
 *
 * ⚠ THE BUTTONS ARE THE MACHINE. Every action comes from `ctsNextStates()` — the same transition
 * table `@grc/shared` gives the api — filtered by what THIS viewer may do. The gate is DERIVED
 * from the machine's own role model (`by: 'assignee' | 'reviewer'`), never re-implemented here: a
 * surface with its own idea of what happens next is a second state machine that agrees with the
 * first only until someone edits one of them.
 */

const PANEL = 'rounded-[10px] border border-cert-neutral-border bg-bg-surface';
const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';

/**
 * ⚠ ONE GRID DEFINITION DRIVES THE HEADER AND EVERY ROW.
 *
 * The first implementation laid the header out with one set of flex widths and the rows with
 * another that happened to match — and they did not: every value sat to the RIGHT of its label,
 * so the table read as a list of pairs rather than as columns. Two definitions of the same
 * geometry agree only until one of them is edited, and nothing fails when they stop.
 *
 * ⚠ The task column is `minmax(0,1.5fr)` and must NOT be widened: a larger track pushes Workflow,
 * Due and Posture apart and leaves dead space between the control and its status, which reads as
 * missing data rather than as spacing.
 */
const GRID_MINE = 'minmax(0,1.5fr) 150px 158px 150px 200px';
/** The team table adds a LEADING assignee column; everything after it keeps the same tracks. */
const GRID_TEAM = `150px ${GRID_MINE}`;
const GRID_GAP = '14px';

/** The grid lines have to be VISIBLE or the alignment work is invisible too. */
const HEAD_BG = 'rgba(255,255,255,0.035)';
const ROW_LINE = '1px solid rgba(255,255,255,0.09)';

const STATE_TONE: Record<CtsWorkflowState, string> = {
  unassigned: 'bg-slate-500/12 text-slate-300',
  assigned: 'bg-sky-500/12 text-sky-300',
  in_progress: 'bg-blue-500/12 text-blue-300',
  submitted_for_review: 'bg-violet-500/12 text-violet-300',
  returned: 'bg-amber-500/12 text-amber-300',
  approved: 'bg-teal-500/12 text-teal-300',
  completed: 'bg-emerald-500/12 text-emerald-300',
  reopened: 'bg-orange-500/12 text-orange-300',
};
const STATE_DOT: Record<CtsWorkflowState, string> = {
  unassigned: 'bg-slate-400', assigned: 'bg-sky-400', in_progress: 'bg-blue-400',
  submitted_for_review: 'bg-violet-400', returned: 'bg-amber-400', approved: 'bg-teal-400',
  completed: 'bg-emerald-400', reopened: 'bg-orange-400',
};
/**
 * ⚠ CAPR S2 — POSTURE IS THE DERIVED IMPLEMENTATION STATUS (the latest approved decision), never the
 * deprecated `posture` axis. No approved decision is "Not Yet Assessed".
 */
const POSTURE_TONE: Record<CtsImplementationStatus, string> = {
  implemented: 'text-cert-valid-fg', partially_implemented: 'text-cert-warn-fg', not_implemented: 'text-cert-expired-fg',
  not_applicable: 'text-text-secondary', unknown: 'text-text-secondary',
};
const TILE_ACCENT: Record<string, string> = {
  info: 'text-sky-400 bg-sky-400', review: 'text-violet-400 bg-violet-400',
  overdue: 'text-cert-expired-fg bg-cert-expired-fg', due: 'text-cert-warn-fg bg-cert-warn-fg',
  returned: 'text-orange-400 bg-orange-400', unowned: 'text-slate-400 bg-slate-400',
};

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
const isOpen = (t: Task) => (CTS_OPEN_STATES as readonly string[]).includes(t.workflow_status);
const isOverdue = (t: Task) => t.due_at !== null && new Date(t.due_at) < new Date() && isOpen(t);

const TABS = ['tasks', 'evidence', 'exceptions', 'activity'] as const;
type Tab = typeof TABS[number];
const TAB_LABEL: Record<Tab, string> = {
  tasks: 'Tasks', evidence: 'Evidence', exceptions: 'Exceptions', activity: 'Program activity',
};

// ── Pieces ────────────────────────────────────────────────────────────────────────────────────

function StatePill({ state }: { state: CtsWorkflowState }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-[6px] rounded-full px-[10px] py-[3px] text-[11.5px] font-medium',
      STATE_TONE[state])}>
      <span className={cn('h-[5px] w-[5px] rounded-full', STATE_DOT[state])} />
      {CTS_WORKFLOW_LABEL[state]}
    </span>
  );
}

/** The six fixed-slot indicators, matching the Controls workspace exactly. */
function Indicators({ task }: { task: Task }) {
  const items = [
    // ⚠ ASSIGNEE, NOT OWNER. This slot reads `assignee_user_id` — the workflow axis — and for a
    //    while it was labelled "Owner", which is the OTHER axis: `cts_control_state`'s RACI
    //    columns, a different table and a different question. A retro-walk caught it exactly as
    //    you would expect: the chip named an owner while the control's Owner card, correctly,
    //    showed none. ⓘ The copy was carried over from the Controls workspace's matching slot,
    //    which reads `owner_name` and is right to say Owner — the words moved and the data
    //    source did not.
    { Icon: User, on: task.assignee_user_id !== null, title: task.assignee_name ? `Assignee — ${task.assignee_name}` : 'Assignee — nobody' },
    { Icon: Paperclip, on: false, title: 'Evidence — shown on the control', dim: true },
    { Icon: Radio, on: task.implementation_status !== null, title: task.implementation_status !== null ? 'Monitoring — assessment approved' : 'Monitoring — not yet assessed' },
    { Icon: FileText, on: false, title: 'Policy document linked — not available in this release', dim: true },
    { Icon: Grid2x2, on: task.frameworks.length > 0, title: task.frameworks.length > 0 ? `Framework mapping — ${task.frameworks.length}` : 'Framework mapping — none' },
    { Icon: CircleCheck, on: task.workflow_status === 'approved' || task.workflow_status === 'completed',
      warn: ['submitted_for_review', 'returned', 'reopened'].includes(task.workflow_status),
      title: `Approval — ${CTS_WORKFLOW_LABEL[task.workflow_status].toLowerCase()}` },
  ];
  return (
    <span className="flex shrink-0 items-center gap-[11px]">
      {items.map(({ Icon, on, warn, dim, title }, i) => (
        <span key={i} title={title} className="inline-flex">
          <Icon aria-hidden="true" strokeWidth={1.3}
            className={cn('h-[14px] w-[14px]',
              dim ? 'text-[#333c4b]' : warn ? 'text-[#f5b74d]' : on ? 'text-[#3ecf8e]' : 'text-[#333c4b]')} />
        </span>
      ))}
    </span>
  );
}

function Distribution({ title, caption, segs }: { title: string; caption: string; segs: DistributionSegment[] }) {
  const total = segs.reduce((n, s) => n + s.n, 0);
  return (
    <div className={cn(PANEL, 'p-[16px]')}>
      <div className="mb-[11px] flex flex-wrap items-baseline gap-[8px]">
        <span className="text-[13px] font-semibold text-text-primary">{title}</span>
        <span className="text-[11.5px] text-text-faint">· {caption}</span>
      </div>
      {total === 0 ? (
        <p className="text-[12px] italic text-text-faint">Nothing to show yet.</p>
      ) : (
        <>
          {/* ⚠ ONE rounded track, segments inside it — not a row of separate bars. */}
          <div className="flex h-[8px] w-full overflow-hidden rounded-[5px] bg-bg-surface-2">
            {segs.map((s) => (
              <div key={s.key} className={cn('h-full', s.cls)} style={{ width: `${(s.n / total) * 100}%` }} />
            ))}
          </div>
          <div className="mt-[11px] flex flex-wrap items-center gap-x-[18px] gap-y-[7px]">
            {segs.map((s) => (
              <span key={s.key} className="flex items-baseline gap-[7px] text-[12px] text-text-secondary">
                <span className={cn('h-[8px] w-[8px] shrink-0 rounded-[2px]', s.cls)} />
                {s.label} <span className="tabular-nums text-text-primary">{s.n}</span>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Actions({ task, isReviewer, viewerId, onMove, busy }: {
  task: Task; isReviewer: boolean; viewerId: string | null;
  onMove: (to: CtsWorkflowState, needsReason: boolean) => void; busy: boolean;
}) {
  const moves = ctsNextStates(task.workflow_status).filter((t) => (
    // ⚠ Derived from the machine's own role model. The two DECISIONS follow `viewer_can_decide` — the
    //    SERVER's per-row answer (the named Approver alone, else the reviewer floor), never a role
    //    check here. `assignee` is a ROW fact — is this mine — which is why the viewer's id must come
    //    from the server rather than be inferred from the rows being gated.
    t.by === 'reviewer' ? task.viewer_can_decide
      // ⚠ CAPR S2 (SCR) — the SUBMIT writes the proposal, so only the assignee is offered it; the
      //   reviewer exemption covers the non-substance moves only (the server refuses the rest, 403).
      : t.to === 'submitted_for_review' ? task.assignee_user_id === viewerId
      : (isReviewer || task.assignee_user_id === viewerId)
  ));
  if (moves.length === 0) return null;
  // Design labels the primary action by intent rather than by target state — "Resume" reads
  // better than "In Progress" on a returned row — while the value sent is still the machine's.
  const label = (to: CtsWorkflowState) => {
    if (to === 'returned') return 'Return with changes';
    if (to === 'in_progress') return task.workflow_status === 'returned' || task.workflow_status === 'reopened' ? 'Resume' : 'Start';
    if (to === 'approved') return 'Approve';
    // ⓘ CAPR S1/S2: "Mark complete" and "Reopen" are gone — `approved` is terminal and a correction is
    //   a NEW assessment, so no move out of an approved row exists to label.
    return CTS_WORKFLOW_LABEL[to];
  };
  return (
    <span className="flex flex-wrap items-start justify-end gap-[7px]">
      {moves.map((m, i) => m.to === 'submitted_for_review' ? (
        // ⚠ CAPR S2 — A SUBMISSION CARRIES THE PROPOSAL, which is written on the control's assessment
        //   screen. Submitting from a list row would send no status, so the row goes there instead.
        <Link key={m.to} to={`/compliance/workspace/controls/${task.control_reference}?tab=tasks`}
          className={cn('rounded-[7px] px-[12px] py-[7px] text-[12.5px] font-medium transition-colors',
            i === 0 ? 'bg-accent-cyan text-white hover:opacity-90'
              : 'border border-cert-neutral-border text-text-secondary hover:text-text-primary')}>
          {task.proposed_implementation_status ? 'Continue assessment' : 'Assess'}
        </Link>
      ) : (
        <button
          key={m.to} type="button" disabled={busy}
          onClick={() => onMove(m.to, m.requiresReason === true)}
          className={cn('rounded-[7px] px-[12px] py-[7px] text-[12.5px] font-medium transition-colors disabled:opacity-50',
            i === 0 && m.to !== 'returned'
              ? 'bg-accent-cyan text-white hover:opacity-90'
              : 'border border-cert-neutral-border text-text-secondary hover:text-text-primary')}
        >
          {label(m.to)}
        </button>
      ))}
    </span>
  );
}

function TaskRow({ task, isReviewer, viewerId, onMove, busy, grid, assignee }: {
  task: Task; isReviewer: boolean; viewerId: string | null;
  onMove: (t: Task, to: CtsWorkflowState, needsReason: boolean) => void;
  busy: boolean; grid: string; assignee?: boolean;
}) {
  const due = fmt(task.due_at);
  const overdue = isOverdue(task);
  return (
    <div className="px-[16px] py-[14px]" style={{ borderTop: ROW_LINE }}>
      <div className="grid items-start" style={{ gridTemplateColumns: grid, gap: GRID_GAP }}>
        {assignee && (
          <span className="min-w-0 truncate text-[12.5px] text-text-secondary">
            {task.assignee_name ?? <span className="text-text-faint">Unassigned</span>}
          </span>
        )}
        <div className="min-w-0">
          {/*
            ⚠ THE CONTROL'S TITLE IS THE HEADLINE, because a task has no title of its own.
            Design's mock shows task names that no column holds — `cts_assignments` records WHO,
            WHEN and WHERE-IN-THE-MACHINE, never a label. Generating one would read as authored
            text and be wrong the moment anyone relied on it. Flagged to PE.
          */}
          <span className="block text-[13.5px] font-semibold text-text-primary">{task.control_title}</span>
          <Link to={`/compliance/workspace/controls/${task.control_reference}`}
            className="mt-[2px] inline-flex items-center gap-[6px] text-[12px] transition-colors hover:opacity-80">
            <span className="font-mono text-accent-cyan">{task.control_code}</span>
            <ArrowUpRight className="h-[11px] w-[11px] text-accent-cyan" aria-hidden="true" />
          </Link>
          {task.control_description && (
            <p className="mt-[5px] text-[12.5px] leading-[1.5] text-text-secondary">
              {task.control_description}
            </p>
          )}
          {/*
            ⚠ ONE FLOWING LINE: icons, a thin divider, then chips — left-aligned with the
            description above so the cell reads as a single block rather than three stacked
            fragments. Every chip is its own flex child, so they fill the line beside the icons and
            wrap only when the track genuinely runs out of room; the icon cluster is `shrink-0` so
            it never squashes, but it is a single child and so never forces a break by itself.
          */}
          <div className="mt-[8px] flex min-w-0 flex-wrap items-center gap-x-[8px] gap-y-[6px]">
            <Indicators task={task} />
            {task.frameworks.length > 0 && <span className="h-[13px] w-px shrink-0 bg-white/12" />}
            {task.frameworks.map((f) => (
              <span key={f} className="shrink-0 rounded-[4px] bg-bg-surface-2 px-[8px] py-[2px] font-mono text-[10.5px] text-text-secondary">{f}</span>
            ))}
          </div>
        </div>

        <span><StatePill state={task.workflow_status} /></span>
        <span className={cn('text-[12.5px]', overdue ? 'font-medium text-cert-expired-fg' : 'text-text-secondary')}>
          {/* ⚠ A missing deadline is not a missed one — collapsing them manufactures urgency. */}
          {due ? (overdue ? `Overdue — was due ${due}` : `Due ${due}`) : <span className="italic text-text-faint">No due date</span>}
        </span>
        <span className={cn('text-[12.5px]', task.implementation_status ? POSTURE_TONE[task.implementation_status] : 'italic text-text-faint')}>
          {/* ⚠ Not yet assessed is NOT "Not Implemented" — nobody has approved an assessment. */}
          {task.implementation_status ? CTS_IMPLEMENTATION_STATUS_LABEL[task.implementation_status] : CTS_NOT_YET_ASSESSED_LABEL}
        </span>
        <Actions task={task} isReviewer={isReviewer} viewerId={viewerId} busy={busy}
          onMove={(to, needsReason) => onMove(task, to, needsReason)} />
      </div>

      {task.returned_reason && task.workflow_status === 'returned' && (
        <div className="mt-[10px] flex items-start gap-[10px] rounded-[7px] border border-amber-500/25 bg-amber-500/8 px-[12px] py-[9px]">
          <CornerUpLeft className="mt-[2px] h-[12px] w-[12px] shrink-0 text-amber-300" aria-hidden="true" />
          <p className="text-[12.5px] leading-[1.5] text-amber-100/90">
            {/*
              ⚠ THE REASON IS THE POINT OF "RETURNED" AND IT REACHES THE ASSIGNEE. One state with a
              payload, never a bare rejection badge — otherwise they have to go and ask why. It sits
              BELOW the grid rather than inside a cell, because it belongs to the whole row.
            */}
            <span className={cn(EYEBROW, 'mr-[8px] text-amber-300')}>Returned</span>
            {task.returned_reason}
          </p>
        </div>
      )}
    </div>
  );
}

/** One select, so every filter carries its label the same way and none drifts in styling. */
function FilterSelect({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <label className="flex items-center gap-[7px] rounded-[8px] border border-cert-neutral-border bg-bg-surface px-[10px] py-[7px]">
      <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-faint">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={`Filter by ${label.toLowerCase()}`}
        className="max-w-[190px] bg-transparent text-[12.5px] text-text-secondary focus:outline-none">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

/** The header — same grid, same gap, same source constant as the rows it labels. */
function TableHead({ grid, assignee }: { grid: string; assignee?: boolean }) {
  return (
    <div className="grid px-[16px] py-[10px]"
      style={{ gridTemplateColumns: grid, gap: GRID_GAP, background: HEAD_BG }}>
      {assignee && <span className={EYEBROW}>Assignee</span>}
      <span className={EYEBROW}>Task / Control</span>
      <span className={EYEBROW}>Workflow</span>
      <span className={EYEBROW}>Due</span>
      <span className={EYEBROW}>Posture</span>
      <span />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────────────────────

export function TasksWorkspacePage() {
  const { data, isLoading, isError } = useTasksWorkspace();
  const move = useTransitionTask();
  const evidence = useEvidenceRepository();
  const exceptions = useExceptionsRegister();
  const activity = useProgramActivity();

  /**
   * ⚠ THE TAB LIVES IN THE URL, not in component state.
   *
   * The retired legacy pages redirect here, and each must land on the tab that replaced it —
   * which is only possible if a tab is addressable. ⓘ An unrecognised value falls back to
   * `tasks` rather than rendering nothing: a stale bookmark should degrade, not break.
   */
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(raw ?? '') ? (raw as Tab) : 'tasks';
  const setTab = (t: Tab) => setParams(t === 'tasks' ? {} : { tab: t }, { replace: true });
  const [showInsights, setShowInsights] = useState(true);
  /**
   * ⚠ SEARCH AND FILTERS BELONG TO THE TEAM SECTION, NOT TO "MY ASSIGNMENTS".
   *
   * The personal queue is short and action-first — if it were long enough to need searching, the
   * problem is the queue rather than the page. Putting a filter bar above it would also invite the
   * reader to narrow the one list they are meant to work through top to bottom.
   */
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState<CtsWorkflowState | 'all'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [dueFilter, setDueFilter] = useState<'all' | 'overdue' | 'soon' | 'none'>('all');
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [controlFilter, setControlFilter] = useState('all');
  const [pending, setPending] = useState<{ task: Task; to: CtsWorkflowState } | null>(null);
  const [reason, setReason] = useState('');
  // ⚠ CAPR S2 — A REFUSED MOVE IS SAID, NEVER SWALLOWED. The page had no error UI for a move at all,
  //   so the S1 Approve refusal was completely silent. The server's reason, told apart by code.
  const [moveError, setMoveError] = useState<string | null>(null);

  const insights = useMemo(() => (data ? buildInsights(data) : []), [data]);
  const wfDist = useMemo(() => (data ? workflowDistribution(data.all) : []), [data]);
  const posDist = useMemo(() => (data ? postureDistribution(data.all) : []), [data]);

  /**
   * ⚠ MY ASSIGNMENTS = MY WORK **AND** MY REVIEWS. A review I owe is action-first work; putting it
   * on a separate surface would mean the page answering "what should I do next" left out half the
   * answer. Reviews only appear for a reviewer, because for anyone else the machine refuses them.
   */
  const mineAndReviews = useMemo(() => {
    if (!data) return [];
    // ⚠ CAPR S2 — the reviews I OWE are the ones the server says I may decide (`viewer_can_decide`),
    //   so a named Approver without a reviewer role gets their queue and a floor reviewer is not
    //   handed work the named Approver alone may decide.
    const reviews = data.all.filter((t) => t.workflow_status === 'submitted_for_review'
      && t.viewer_can_decide && !data.mine.some((m) => m.id === t.id));
    return [...data.mine, ...reviews];
  }, [data]);

  /** ⚠ Any filter set at all — the Clear link exists only when there is something to clear. */
  const filtersActive = q.trim() !== '' || stateFilter !== 'all' || assigneeFilter !== 'all'
    || dueFilter !== 'all' || frameworkFilter !== 'all' || controlFilter !== 'all';

  const clearFilters = () => {
    setQ(''); setStateFilter('all'); setAssigneeFilter('all');
    setDueFilter('all'); setFrameworkFilter('all'); setControlFilter('all');
  };

  /** The options come from the DATA, so a filter can never offer a value that matches nothing. */
  const filterOptions = useMemo(() => {
    const rows = data?.all ?? [];
    const assignees = new Map<string, string>();
    const frameworks = new Set<string>();
    const controls = new Map<string, string>();
    for (const t of rows) {
      if (t.assignee_user_id) assignees.set(t.assignee_user_id, t.assignee_name ?? 'Unnamed');
      t.frameworks.forEach((f) => frameworks.add(f));
      controls.set(t.control_reference, `${t.control_code} · ${t.control_title}`);
    }
    return {
      assignees: [...assignees.entries()].sort((a, b) => a[1].localeCompare(b[1])),
      frameworks: [...frameworks].sort(),
      controls: [...controls.entries()].sort((a, b) => a[1].localeCompare(b[1])),
    };
  }, [data]);

  const teamRows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    const now = new Date();
    return data.all.filter((t) => {
      if (stateFilter !== 'all' && t.workflow_status !== stateFilter) return false;
      if (assigneeFilter !== 'all') {
        // ⚠ "Unassigned" here means an open row with NO OWNER — not the `unassigned` STATE, which
        //    is the absence of a row and can never appear in a task list.
        if (assigneeFilter === 'none' ? t.assignee_user_id !== null : t.assignee_user_id !== assigneeFilter) return false;
      }
      if (dueFilter !== 'all') {
        const open = isOpen(t);
        const d = t.due_at ? new Date(t.due_at) : null;
        // ⚠ "No due date" is its own option rather than a bucket of "overdue" — a missing deadline
        //    is not a missed one.
        if (dueFilter === 'none' && d !== null) return false;
        if (dueFilter === 'overdue' && !(d !== null && d < now && open)) return false;
        if (dueFilter === 'soon' && !(d !== null && open && d >= now
          && d.getTime() - now.getTime() <= 7 * 86_400_000)) return false;
      }
      if (frameworkFilter !== 'all' && !t.frameworks.includes(frameworkFilter)) return false;
      if (controlFilter !== 'all' && t.control_reference !== controlFilter) return false;
      if (!needle) return true;
      return `${t.control_code} ${t.control_title} ${t.assignee_name ?? ''}`.toLowerCase().includes(needle);
    });
  }, [data, q, stateFilter, assigneeFilter, dueFilter, frameworkFilter, controlFilter]);

  const onMove = (task: Task, to: CtsWorkflowState, needsReason: boolean) => {
    setMoveError(null);
    // ⚠ An approval is CONFIRMED against the proposal it approves (shown in the dialog), never one click.
    if (needsReason || to === 'approved') { setPending({ task, to }); setReason(''); return; }
    // ⓘ A one-click move is sent as the machine's target alone; a refusal surfaces through the
    //   mutation's own error state (the banner below), so the call carries nothing else.
    move.mutate({ id: task.id, to });
  };
  const closePending = () => { setPending(null); setMoveError(null); };

  if (isLoading) {
    return <div className="space-y-[12px] p-[24px]"><Skeleton className="h-[28px] w-[200px]" /><Skeleton className="h-[300px] w-full" /></div>;
  }
  if (isError || !data) {
    return <div className="p-[24px] text-[13px] text-text-secondary">Tasks could not be loaded.</div>;
  }

  const counts: Record<Tab, number> = {
    tasks: data.all.length, evidence: evidence.data?.length ?? 0,
    exceptions: exceptions.data?.length ?? 0, activity: activity.data?.length ?? 0,
  };
  const stateCounts = data.all.reduce<Record<string, number>>((a, t) => {
    a[t.workflow_status] = (a[t.workflow_status] ?? 0) + 1; return a;
  }, {});

  return (
    <div className="p-[24px]">
      <header className="mb-[16px]">
        <h1 className="text-[24px] font-bold tracking-[-0.015em] text-text-primary">Tasks</h1>
        <p className="mt-[5px] max-w-[80ch] text-[13.5px] text-text-secondary">
          A unified workspace for assignments, reviews, evidence, exceptions, and compliance activity.
        </p>
      </header>

      <div role="tablist" aria-label="Workspace" className="mb-[20px] flex flex-wrap gap-[6px] border-b border-border-subtle">
        {TABS.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={cn('flex items-center gap-[8px] border-b-2 px-[12px] pb-[10px] pt-[4px] text-[13.5px] transition-colors',
              tab === t ? 'border-accent-cyan font-semibold text-text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary')}>
            {TAB_LABEL[t]}
            {/* ⚠ No badge at zero — "0" reads as a measured result rather than an empty tab. */}
            {counts[t] > 0 && (
              <span className="rounded-full bg-bg-surface-2 px-[7px] py-[1px] text-[11px] tabular-nums text-text-secondary">
                {counts[t]}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'tasks' && (
        <>
          {/* ⚠ A one-click move that the server refused says so here, in its own words. */}
          {move.isError && !pending && (
            <p className="mb-[14px] rounded-[8px] border border-amber-500/25 bg-amber-500/8 px-[12px] py-[9px] text-[12.5px] text-amber-100/90"
              role="alert">{workflowErrorText(move.error)}</p>
          )}
          {showInsights && (
            <section className="mb-[22px]">
              <div className="mb-[11px] flex items-center gap-[9px]">
                <BarChart3 className="h-[13px] w-[13px] text-accent-cyan" aria-hidden="true" />
                <span className={EYEBROW}>Insights · what needs attention</span>
                <button type="button" onClick={() => setShowInsights(false)}
                  className="ml-auto text-[12px] text-text-secondary transition-colors hover:text-text-primary">
                  Hide insights
                </button>
              </div>
              <div className="mb-[14px] grid gap-[12px] sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
                {insights.map((t) => (
                  <div key={t.key} className={cn(PANEL, 'flex flex-col px-[16px] pb-[14px] pt-[15px]')}>
                    {/*
                      ⚠ A ZERO IS MUTED, NOT ACCENTED. An accent colour is an attention signal, and
                      nothing needing attention is exactly what a zero says — colouring it red or
                      amber would make "no overdue work" look like a problem.
                    */}
                    <span className={cn('text-[26px] font-bold leading-none tabular-nums',
                      t.value === 0 ? 'text-text-faint' : TILE_ACCENT[t.tone].split(' ')[0])}>
                      {t.value}
                    </span>
                    <span className="mt-[9px] text-[13px] font-medium text-text-primary">{t.label}</span>
                    {/* ⚠ The caption carries the SCOPE — "mine" versus "team" is the difference
                        between a queue and a report, and a bare number implies the former. */}
                    <span className="mt-[3px] text-[11.5px] text-text-secondary">{t.caption}</span>
                    {/* ⚠ mt-auto pins the accent to the BOTTOM EDGE, so a taller neighbour cannot
                        leave one tile's bar floating mid-card. */}
                    <span className={cn('mt-auto pt-[11px]')}>
                      <span className={cn('block h-[2px] w-full rounded-full opacity-70',
                        t.value === 0 ? 'bg-text-faint/40' : TILE_ACCENT[t.tone].split(' ')[1])} />
                    </span>
                  </div>
                ))}
              </div>
              <div className="grid gap-[12px] lg:grid-cols-2">
                <Distribution title="Workflow distribution" caption="team work status" segs={wfDist} />
                {/* ⚠ Two panels, side by side, NOT one bar. Posture is a separate axis; a single
                    stacked bar carrying both would invite the reader to add them together. */}
                <Distribution title="Control posture" caption="assurance — a separate axis" segs={posDist} />
              </div>
            </section>
          )}
          {!showInsights && (
            <button type="button" onClick={() => setShowInsights(true)}
              className="mb-[18px] flex items-center gap-[8px] text-[12px] text-text-secondary transition-colors hover:text-text-primary">
              <BarChart3 className="h-[13px] w-[13px]" aria-hidden="true" /> Show insights
            </button>
          )}

          {/*
            ⚠ Each section is a NAMED region. The claim "My assignments is search-free" is only
            checkable if the boundary between the personal queue and the team surface exists in the
            markup — otherwise a test can only assert that A search box exists somewhere on a page
            that has one either way.
          */}
          <section className="mb-[24px]" aria-labelledby="mine-h">
            <div className="mb-[4px] flex flex-wrap items-baseline gap-[12px]">
              <h2 id="mine-h" className="text-[18px] font-bold text-text-primary">My assignments</h2>
              <span className="font-mono text-[12px] text-text-secondary">
                {data.mine.length} assigned
                {data.viewer_is_reviewer && ` · ${mineAndReviews.length - data.mine.length} to review`}
              </span>
            </div>
            <p className="mb-[12px] text-[13px] text-text-secondary">
              Your assigned work, pending reviews, and actions requiring your attention.
            </p>
            <div className={cn(PANEL, 'overflow-hidden')}>
              <TableHead grid={GRID_MINE} />
              {mineAndReviews.length === 0 ? (
                <p className="flex items-center gap-[9px] px-[16px] py-[20px] text-[13px] text-text-secondary">
                  <CheckCircle2 className="h-[15px] w-[15px] shrink-0 text-cert-valid-fg" aria-hidden="true" />
                  {/* ⚠ "Nothing assigned to you" is NOT "nothing to do" — the team table below may
                      be full, and one blank state for both would let an idle queue read as a
                      finished programme. */}
                  Nothing is assigned to you right now.
                </p>
              ) : mineAndReviews.map((t) => (
                <TaskRow key={t.id} task={t} isReviewer={data.viewer_is_reviewer}
                  viewerId={data.viewer_user_id} busy={move.isPending} onMove={onMove}
                  grid={GRID_MINE} />
              ))}
            </div>
          </section>

          <section className="mb-[22px]" aria-labelledby="workload-h">
            <h2 id="workload-h" className={cn(EYEBROW, 'mb-[10px] block')}>Team workload</h2>
            <div className={cn(PANEL, 'px-[16px] py-[14px]')}>
              {data.workload.length === 0 ? (
                <p className="text-[12.5px] italic text-text-faint">Nobody has open work right now.</p>
              ) : data.workload.map((w) => {
                const max = Math.max(...data.workload.map((x) => x.open), 1);
                return (
                  <div key={w.user_id ?? 'unassigned'} className="mb-[8px] flex items-center gap-[12px] last:mb-0">
                    <span className="w-[140px] shrink-0 truncate text-[12.5px] text-text-secondary">
                      {w.name ?? <span className="text-text-faint">Unassigned</span>}
                    </span>
                    <span className="h-[7px] min-w-0 flex-1 overflow-hidden rounded-full bg-bg-surface-2">
                      <span className="block h-full bg-accent-cyan/70" style={{ width: `${(w.open / max) * 100}%` }} />
                    </span>
                    <span className="w-[160px] shrink-0 text-right text-[12px] tabular-nums text-text-secondary">
                      {w.open} open
                      {/* ⚠ Overdue is a DIFFERENT FACT from volume, not a shade of it. Three items
                          with two overdue needs help before eight on track — a single bar would
                          rank them the other way round. */}
                      {w.overdue > 0 && <span className="ml-[8px] text-cert-expired-fg">{w.overdue} overdue</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          <section aria-labelledby="allteam-h">
            <h2 id="allteam-h" className={cn(EYEBROW, 'mb-[10px] block')}>All tasks · team</h2>
            <div className="mb-[12px] space-y-[10px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-[12px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text-faint" aria-hidden="true" />
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search tasks by control, task or assignee" aria-label="Search tasks"
                  className="w-full rounded-[9px] border border-cert-neutral-border bg-bg-surface py-[10px] pl-[36px] pr-[12px] text-[13.5px] text-text-primary placeholder:text-text-faint focus:border-accent-cyan focus:outline-none" />
              </div>
              <div className="flex flex-wrap items-center gap-[8px]">
                <FilterSelect label="Assignee" value={assigneeFilter} onChange={setAssigneeFilter}
                  options={[['all', 'All assignees'], ['none', 'Unassigned'],
                    ...filterOptions.assignees.map(([id, name]) => [id, name] as [string, string])]} />
                <FilterSelect label="Status" value={stateFilter}
                  onChange={(v) => setStateFilter(v as CtsWorkflowState | 'all')}
                  options={[['all', `All states (${data.all.length})`],
                    // ⚠ `unassigned` is excluded: it is the absence of a row, so a task list can
                    //    never contain one. It belongs on the Controls rail, where the unit IS the
                    //    control rather than the assignment.
                    ...CTS_WORKFLOW_STATES.filter((x) => x !== 'unassigned')
                      .map((x) => [x, `${CTS_WORKFLOW_LABEL[x]} (${stateCounts[x] ?? 0})`] as [string, string])]} />
                <FilterSelect label="Due" value={dueFilter}
                  onChange={(v) => setDueFilter(v as 'all' | 'overdue' | 'soon' | 'none')}
                  options={[['all', 'Any due date'], ['overdue', 'Overdue'],
                    ['soon', 'Due within 7 days'], ['none', 'No due date']]} />
                <FilterSelect label="Framework" value={frameworkFilter} onChange={setFrameworkFilter}
                  options={[['all', 'All frameworks'],
                    ...filterOptions.frameworks.map((f) => [f, f] as [string, string])]} />
                <FilterSelect label="Control" value={controlFilter} onChange={setControlFilter}
                  options={[['all', 'All controls'],
                    ...filterOptions.controls.map(([ref, label]) => [ref, label] as [string, string])]} />
                {/*
                  ⚠ THE CLEAR LINK EXISTS ONLY WHEN THERE IS SOMETHING TO CLEAR. A permanently
                  visible "Clear filters" reads as a filter being active, which is exactly the
                  confusion it is meant to resolve — and a disabled one is a control that has to be
                  read before it can be ignored.
                */}
                {filtersActive && (
                  <button type="button" onClick={clearFilters}
                    className="ml-auto text-[12.5px] font-medium text-accent-cyan transition-opacity hover:opacity-80">
                    Clear filters
                  </button>
                )}
              </div>
            </div>
            <div className={cn(PANEL, 'overflow-hidden')}>
              <TableHead grid={GRID_TEAM} assignee />
              {teamRows.length === 0 ? (
                <p className="px-[16px] py-[20px] text-[13px] italic text-text-faint"
                  style={{ borderTop: ROW_LINE }}>
                  {data.all.length === 0
                    ? 'No work has been assigned yet. Open a control and use the Workflow card on its Overview tab to assign it.'
                    : 'No tasks match the current filters.'}
                </p>
              ) : teamRows.map((t) => (
                <TaskRow key={t.id} task={t} isReviewer={data.viewer_is_reviewer}
                  viewerId={data.viewer_user_id} busy={move.isPending} onMove={onMove}
                  grid={GRID_TEAM} assignee />
              ))}
            </div>
          </section>
        </>
      )}

      {tab === 'evidence' && <EvidencePanel rows={evidence.data ?? []} />}
      {tab === 'exceptions' && <ExceptionsPanel rows={exceptions.data ?? []} />}
      {tab === 'activity' && <ActivityPanel rows={activity.data ?? []} />}

      {pending && pending.to === 'approved' && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/55 p-[20px] pt-[14vh]"
          onClick={(e) => { if (e.target === e.currentTarget) closePending(); }}>
          <div role="dialog" aria-modal="true" aria-labelledby="approve-title"
            className="w-full max-w-[500px] rounded-[12px] border border-cert-neutral-border bg-bg-base p-[22px]">
            <h2 id="approve-title" className="text-[15.5px] font-semibold text-text-primary">
              Approve this assessment?
            </h2>
            <p className="mt-[6px] text-[12.5px] font-medium text-accent-cyan">
              {pending.task.control_code} · {pending.task.control_title}
            </p>
            {/* ⚠ WHAT IS BEING APPROVED IS SHOWN — the persisted proposal, exactly as submitted. */}
            <p className="mt-[12px] text-[12.5px] text-text-primary">
              Proposed status: {pending.task.proposed_implementation_status
                ? CTS_IMPLEMENTATION_STATUS_LABEL[pending.task.proposed_implementation_status]
                : <span className="text-cert-warn-fg">none recorded — return it so the assessor can record one</span>}
            </p>
            {pending.task.proposed_rationale && (
              <p className="mt-[6px] whitespace-pre-wrap text-[12.5px] leading-[1.6] text-text-secondary">{pending.task.proposed_rationale}</p>
            )}
            <p className="mt-[6px] text-[12px] text-text-secondary">
              {(pending.task.proposed_evidence ?? []).length === 0 ? NO_EVIDENCE_LABEL
                : `${(pending.task.proposed_evidence ?? []).length} evidence item(s), each recorded as unverified at approval`}
              {' · '}
              <Link to={`/compliance/workspace/controls/${pending.task.control_reference}?tab=tasks`} className="text-accent-cyan hover:opacity-80">
                Review in full on the control
              </Link>
            </p>
            {moveError && <p className="mt-[10px] text-[12px] text-cert-warn-fg" role="alert">{moveError}</p>}
            <div className="mt-[18px] flex items-center justify-end gap-[10px]">
              <button type="button" onClick={closePending}
                className="rounded-[7px] border border-cert-neutral-border px-[14px] py-[8px] text-[12.5px] text-text-secondary hover:text-text-primary">
                Cancel
              </button>
              <button type="button" disabled={move.isPending || !pending.task.proposed_implementation_status}
                onClick={() => move.mutate({ id: pending.task.id, to: 'approved' },
                  { onSuccess: closePending, onError: (err) => setMoveError(workflowErrorText(err)) })}
                className="rounded-[7px] bg-accent-cyan px-[14px] py-[8px] text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-40">
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {pending && pending.to !== 'approved' && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/55 p-[20px] pt-[14vh]"
          onClick={(e) => { if (e.target === e.currentTarget) closePending(); }}>
          <div role="dialog" aria-modal="true" aria-labelledby="return-title"
            className="w-full max-w-[500px] rounded-[12px] border border-cert-neutral-border bg-bg-base p-[22px]">
            <h2 id="return-title" className="text-[15.5px] font-semibold text-text-primary">
              Return this work with changes?
            </h2>
            <p className="mt-[6px] text-[12.5px] font-medium text-accent-cyan">
              {pending.task.control_code} · {pending.task.control_title}
            </p>
            <p className="mt-[12px] text-[12.5px] leading-[1.6] text-text-secondary">
              The assignee will see your reason and can pick the work back up. Their evidence and
              assessments are kept.
            </p>
            <label htmlFor="return-reason" className={cn(EYEBROW, 'mt-[16px] block')}>What needs to change</label>
            <textarea id="return-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              className="mt-[6px] w-full rounded-[8px] border border-cert-neutral-border bg-bg-surface px-[11px] py-[9px] text-[13px] text-text-primary focus:border-accent-cyan focus:outline-none" />
            {moveError && <p className="mt-[10px] text-[12px] text-cert-warn-fg" role="alert">{moveError}</p>}
            <div className="mt-[18px] flex items-center justify-end gap-[10px]">
              <button type="button" onClick={closePending}
                className="rounded-[7px] border border-cert-neutral-border px-[14px] py-[8px] text-[12.5px] text-text-secondary hover:text-text-primary">
                Cancel
              </button>
              <button type="button" disabled={reason.trim() === '' || move.isPending}
                onClick={() => move.mutate({ id: pending.task.id, to: pending.to, reason: reason.trim() },
                  { onSuccess: closePending, onError: (err) => setMoveError(workflowErrorText(err)) })}
                className="rounded-[7px] bg-accent-cyan px-[14px] py-[8px] text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-40">
                Return with changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
